import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { useAuthStore } from "@/lib/api-functions/api-auth";
import { useSimplifiedUserData } from "@/providers/SimplifiedUserDataProvider";
import { useToast } from "@/hooks/use-toast";
import {
  DocumentItem,
  fetchDocumentItems,
  createDocumentItem,
  updateDocumentItem,
  deleteDocumentItem,
  documentStore,
  DocumentData,
  getCachedDocumentItems,
} from "@/lib/stores/documentStore";

// Create context with document-specific interface
interface DocumentDataContextType {
  documents: DocumentItem[];
  isLoading: boolean;
  error: string | null;
  loadData: () => Promise<void>;
  createDocument: (item: {
    game_id: string;
    name: string;
    data: DocumentData[];
    settings?: Record<string, unknown>;
    tags?: string[];
    folder?: string;
  }) => Promise<DocumentItem>;
  updateDocument: (item: {
    id: string;
    game_id: string;
    name: string;
    data: DocumentData[];
    settings?: Record<string, unknown>;
    tags?: string[];
  }) => Promise<DocumentItem>;
  deleteDocument: (id: string, gameId: string) => Promise<void>;
}

const DocumentDataContext = createContext<DocumentDataContextType | null>(null);

// Separate component that only renders when authenticated and has a selected game
const AuthenticatedDocumentDataProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { user } = useAuthStore();
  const { selectedGame } = useSimplifiedUserData();
  const { toast } = useToast();
  const [isInitialized, setIsInitialized] = useState(false);
  const [documents, setDocuments] = useState<DocumentItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    if (!selectedGame?.id) return;

    setIsLoading(true);
    setError(null);
    try {
      // fetchDocumentItems now returns cached data immediately if available
      const fetchedDocuments = await fetchDocumentItems(selectedGame.id, toast);
      setDocuments(fetchedDocuments);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to load documents";
      setError(message);
      console.error("DocumentDataProvider - Error loading documents:", err);
    } finally {
      setIsLoading(false);
    }
  }, [selectedGame?.id, toast]);

  const createDocument = async (item: {
    game_id: string;
    name: string;
    data: DocumentData[];
    settings?: Record<string, unknown>;
    tags?: string[];
    folder?: string;
  }) => {
    const newDocument = await createDocumentItem(item, toast);
    return newDocument;
  };

  const updateDocument = async (item: {
    id: string;
    game_id: string;
    name: string;
    data: DocumentData[];
    settings?: Record<string, unknown>;
    tags?: string[];
  }) => {
    const updatedDocument = await updateDocumentItem(item, toast);
    // The store is already updated with optimistic update, and our subscription will handle the state update
    return updatedDocument;
  };

  const deleteDocument = async (id: string, gameId: string) => {
    await deleteDocumentItem(id, gameId, toast);
    // The store is already updated with optimistic update, and our subscription will handle the state update
    toast({
      title: "Success",
      description: "Document deleted successfully",
    });
  };

  // Load cached data immediately when game changes, then fetch fresh data
  useEffect(() => {
    if (!selectedGame?.id) return;

    // Load cached data immediately for instant response
    const cachedDocuments = getCachedDocumentItems(selectedGame.id);
    if (cachedDocuments.length > 0) {
      setDocuments(cachedDocuments);
    }
    
    // Always fetch fresh data (which will be fast if cached, or do background sync)
    if (!isInitialized) {
      loadData();
      setIsInitialized(true);
    }
  }, [selectedGame?.id, isInitialized, loadData]);

  // Reset initialization when selected game changes
  useEffect(() => {
    setIsInitialized(false);
  }, [selectedGame?.id]);

  // Subscribe to store changes for real-time updates
  useEffect(() => {
    if (!selectedGame?.id) return;

    const unsubscribe = documentStore.subscribe(() => {
      // Get the latest data from the store and filter by current game
      const allDocuments = documentStore.getData();
      const gameDocuments = allDocuments.filter(
        (doc) => doc.game_id === selectedGame.id
      );
      setDocuments(gameDocuments);
    });

    return () => unsubscribe();
  }, [selectedGame?.id]);

  const value: DocumentDataContextType = {
    documents,
    isLoading,
    error,
    loadData,
    createDocument,
    updateDocument,
    deleteDocument,
  };

  return (
    <DocumentDataContext.Provider value={value}>
      {children}
    </DocumentDataContext.Provider>
  );
};

export const DocumentDataProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { user, isAuthenticated, isLoading: authLoading } = useAuthStore();

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <DocumentDataContext.Provider value={null}>
        {children}
      </DocumentDataContext.Provider>
    );
  }

  // If authenticated, render the authenticated provider
  if (isAuthenticated && user?.id) {
    return (
      <AuthenticatedDocumentDataProvider>
        {children}
      </AuthenticatedDocumentDataProvider>
    );
  }

  // If not authenticated, provide null context
  return (
    <DocumentDataContext.Provider value={null}>
      {children}
    </DocumentDataContext.Provider>
  );
};

export const useDocumentDataContext = () => {
  const context = useContext(DocumentDataContext);
  if (!context) {
    throw new Error(
      "useDocumentDataContext must be used within DocumentDataProvider and user must be authenticated"
    );
  }
  return context;
};
